import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import store from '@/store'

class WebSocketService {
  constructor() {
    this.echo = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.activeChannels = new Map()
  }

  connect() {
    try {
      console.log('🔄 Starting WebSocket connection process...')

      // Check if Echo and Pusher are available
      if (!Echo) {
        throw new Error('Laravel Echo is not available')
      }
      if (!Pusher) {
        throw new Error('Pusher is not available')
      }

      // Get token from store or localStorage as fallback
      const token = store.getters['auth/token'] || localStorage.getItem('token')

      if (!token) {
        throw new Error('No authentication token available')
      }

      console.log(`✅ Token available: ${token.substring(0, 10)}...`)

      // Set up Pusher for Laravel Echo
      window.Pusher = Pusher
      console.log('✅ Pusher set on window object')

      const config = {
        broadcaster: 'pusher',
        key: process.env.VUE_APP_WEBSOCKET_KEY || 'healthy-tantra-key',
        wsHost: process.env.VUE_APP_WEBSOCKET_HOST || '127.0.0.1',
        wsPort: parseInt(process.env.VUE_APP_WEBSOCKET_PORT) || 6001,
        wssPort: parseInt(process.env.VUE_APP_WEBSOCKET_PORT) || 6001,
        forceTLS: process.env.VUE_APP_WEBSOCKET_SCHEME === 'wss',
        encrypted: process.env.VUE_APP_WEBSOCKET_SCHEME === 'wss',
        disableStats: true,
        enabledTransports: ['ws'],
        cluster: 'eu', // Required for Pusher, but ignored by Reverb
        auth: {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          params: {}
        },
        authEndpoint: `${process.env.VUE_APP_API_URL || 'http://127.0.0.1:8000'}/api/broadcasting/auth`,
      }

      console.log('🔌 Connecting to Reverb WebSocket with config:')
      console.log('- Host:', config.wsHost)
      console.log('- Port:', config.wsPort)
      console.log('- Key:', config.key)
      console.log('- Auth Endpoint:', config.authEndpoint)

      console.log('🔄 Creating Echo instance...')
      this.echo = new Echo(config)
      console.log('✅ Echo instance created')

      console.log('🔄 Setting up event listeners...')
      this.setupEventListeners()
      console.log('✅ Event listeners set up')

      console.log('✅ WebSocket service initialization complete')
      return this.echo
    } catch (error) {
      console.error('❌ WebSocket connection error:', error)
      this.handleConnectionError()
      return null
    }
  }

  setupEventListeners() {
    if (!this.echo) return

    // Set up connection event listeners
    this.echo.connector.pusher.connection.bind('connected', () => {
      console.log('✅ WebSocket Connected to Reverb!')
      this.reconnectAttempts = 0

      // Join user's channel for notifications
      const user = store.getters['auth/user']
      if (user) {
        this.joinUserChannel(user.id)
      }
    })

    this.echo.connector.pusher.connection.bind('disconnected', () => {
      console.log('❌ WebSocket Disconnected from Reverb')
      this.handleReconnect()
    })

    this.echo.connector.pusher.connection.bind('error', (error) => {
      console.error('❌ WebSocket Error:', error)
      this.handleConnectionError()
    })

    this.echo.connector.pusher.connection.bind('state_change', (states) => {
      console.log(`🔄 WebSocket State: ${states.previous} → ${states.current}`)
    })

    this.echo.connector.pusher.connection.bind('unavailable', () => {
      console.log('❌ WebSocket connection unavailable')
      this.handleConnectionError()
    })
  }

  handleConnectionError() {
    // Fallback to polling for real-time updates
    console.log('Falling back to HTTP polling for real-time updates')

    // Show user a notification about connection issues
    store.dispatch('errors/setError',
      { message: 'Real-time messaging is temporarily unavailable. Messages will update periodically.' })
  }

  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

      setTimeout(() => {
        this.connect()
      }, Math.pow(2, this.reconnectAttempts) * 1000) // Exponential backoff
    } else {
      console.error('Max reconnection attempts reached')
      this.handleConnectionError()
    }
  }

  disconnect() {
    if (this.echo) {
      this.echo.disconnect()
      this.echo = null
      this.activeChannels.clear()
    }
  }

  // Channel management
  joinUserChannel(userId) {
    if (!this.echo || !userId) return null

    try {
      const channelName = `user.${userId}`
      const channel = this.echo.private(channelName)
      this.activeChannels.set(channelName, channel)
      console.log(`Joined user channel: ${channelName}`)
      return channel
    } catch (error) {
      console.error('Failed to join user channel:', error)
      return null
    }
  }

  joinChatChannel(chatId, isGroup = false) {
    if (!this.echo || !chatId) return null

    try {
      const channelName = isGroup ? `room.${chatId}` : `chat.${chatId}`
      let channel

      if (isGroup) {
        channel = this.echo.join(channelName)
      } else {
        channel = this.echo.private(channelName)
      }

      this.activeChannels.set(channelName, channel)
      console.log(`Joined ${isGroup ? 'group' : 'private'} channel: ${channelName}`)
      return channel
    } catch (error) {
      console.error('Failed to join chat channel:', error)
      return null
    }
  }

  leaveChatChannel(chatId, isGroup = false) {
    if (!this.echo || !chatId) return

    try {
      const channelName = isGroup ? `room.${chatId}` : `chat.${chatId}`

      if (this.activeChannels.has(channelName)) {
        this.echo.leave(channelName)
        this.activeChannels.delete(channelName)
        console.log(`✅ Left ${isGroup ? 'group' : 'private'} channel: ${channelName}`)
      }
    } catch (error) {
      console.error('Failed to leave chat channel:', error)
    }
  }

  // Event listeners for channels
  listenForMessages(channelName, callback) {
    const channel = this.activeChannels.get(channelName)
    if (channel) {
      channel.listen('.MessageSent', callback)
      console.log(`Listening for messages on: ${channelName}`)
    } else {
      console.error(`Channel not found for listening: ${channelName}`)
    }
  }

  listenForTyping(channelName, callback) {
    const channel = this.activeChannels.get(channelName)
    if (channel && channel.listenForWhisper) {
      channel.listenForWhisper('typing', callback)
    }
  }

  sendTyping(channelName, data) {
    const channel = this.activeChannels.get(channelName)
    if (channel && channel.whisper) {
      channel.whisper('typing', data)
    }
  }

  // Presence channel methods for group chats
  onUserJoined(channelName, callback) {
    const channel = this.activeChannels.get(channelName)
    if (channel && channel.joining) {
      channel.joining(callback)
    }
  }

  onUserLeft(channelName, callback) {
    const channel = this.activeChannels.get(channelName)
    if (channel && channel.leaving) {
      channel.leaving(callback)
    }
  }

  onUsersHere(channelName, callback) {
    const channel = this.activeChannels.get(channelName)
    if (channel && channel.here) {
      channel.here(callback)
    }
  }

  // Remove event listeners
  stopListening(channelName, event = null) {
    const channel = this.activeChannels.get(channelName)
    if (channel) {
      if (event) {
        channel.stopListening(event)
      } else {
        // Remove all listeners for the channel
        this.echo.leave(channelName)
        this.activeChannels.delete(channelName)
      }
    }
  }

  // Check connection status
  isConnected() {
    return this.echo && this.echo.connector.pusher.connection.state === 'connected'
  }

  // Get echo instance
  getEcho() {
    return this.echo
  }

  // Get active channels
  getActiveChannels() {
    return Array.from(this.activeChannels.keys())
  }

  // Legacy methods for backward compatibility
  joinUserRoom(userId) {
    console.warn('joinUserRoom is deprecated, use joinUserChannel instead')
    return this.joinUserChannel(userId)
  }

  joinChatRoom(chatId) {
    console.warn('joinChatRoom is deprecated, use joinChatChannel instead')
    return this.joinChatChannel(chatId, false)
  }

  leaveChatRoom(chatId) {
    console.warn('leaveChatRoom is deprecated, use leaveChatChannel instead')
    return this.leaveChatChannel(chatId, false)
  }

  onNewMessage() {
    console.warn('onNewMessage is deprecated, use listenForMessages with channel name instead')
    // This is kept for backward compatibility but should be replaced
  }

  onUserTyping() {
    console.warn('onUserTyping is deprecated, use listenForTyping with channel name instead')
  }

  onUserOnline() {
    console.warn('onUserOnline is deprecated, use onUserJoined with channel name instead')
  }

  onUserOffline() {
    console.warn('onUserOffline is deprecated, use onUserLeft with channel name instead')
  }
}

// Export singleton instance
export default new WebSocketService()